import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { useGlobalAudioVolume } from './GlobalAudioContext';

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

/** Колёсико: 100 px прокрутки (щелчок обычной мыши) — 10 % громкости; тачпад даёт много мелких сдвигов — громкость едет плавно */
const VOLUME_PER_WHEEL_PX = 0.001;
/** Прокрутка строками (Firefox) — в пикселях */
const WHEEL_LINE_PX = 33;

/**
 * Громкость общего плеера: вертикальный ползунок над значком (открыт, пока мышь над ним или его тянут), нажатие на значок —
 * выключить/включить звук, колёсико над значком и ползунком — громче (вверх) и тише (вниз). Только поведение; вид — у плеера
 * (features/newdesign/audio/AudioBar). areaRef — область громкости: колёсико слушается на ней, страница под ней не прокручивается.
 */
export function useGlobalAudioVolumeSlider() {
  const { volume, muted, setVolume, toggleMute } = useGlobalAudioVolume();
  const level = muted ? 0 : volume;
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const areaRef = useRef<HTMLDivElement | null>(null);
  const levelRef = useRef(level);
  levelRef.current = level;

  /** Верх дорожки — 100 %, низ — 0 */
  const ratioFromPointer = useCallback(
    (clientY: number) => {
      const el = trackRef.current;
      if (!el) return level;
      const rect = el.getBoundingClientRect();
      if (rect.height <= 0) return level;
      return clamp01(1 - (clientY - rect.top) / rect.height);
    },
    [level]
  );

  const onTrackPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      draggingRef.current = true;
      setDragging(true);
      setOpen(true);
      e.currentTarget.setPointerCapture(e.pointerId);
      setVolume(ratioFromPointer(e.clientY), { ramp: false });
    },
    [ratioFromPointer, setVolume]
  );

  const onTrackPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      setVolume(ratioFromPointer(e.clientY), { ramp: false });
    },
    [ratioFromPointer, setVolume]
  );

  const onTrackPointerEnd = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* уже отпущен */
    }
  }, []);

  const onTrackKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? 0.1 : 0.05;
      if (e.key === 'ArrowUp' || e.key === 'ArrowRight') {
        e.preventDefault();
        setVolume(level + step);
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') {
        e.preventDefault();
        setVolume(level - step);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setVolume(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setVolume(1);
      }
    },
    [level, setVolume]
  );

  // Слушатель не пассивный: иначе прокрутка ушла бы и странице под плеером (у React onWheel пассивный — preventDefault не сработает)
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const px = e.deltaMode === WheelEvent.DOM_DELTA_LINE ? e.deltaY * WHEEL_LINE_PX : e.deltaY;
      if (px === 0) return;
      e.preventDefault();
      setOpen(true);
      setVolume(clamp01(levelRef.current - px * VOLUME_PER_WHEEL_PX), { ramp: false });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [setVolume]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return {
    /** Громкость, которую слышно: 0 — выключено */
    level,
    open,
    /** Ползунок тянут: бегунок виден и без наведения */
    dragging,
    trackRef,
    areaRef,
    onEnter: () => setOpen(true),
    onLeave: () => {
      if (!draggingRef.current) setOpen(false);
    },
    toggleMute,
    onTrackPointerDown,
    onTrackPointerMove,
    onTrackPointerEnd,
    onTrackKeyDown,
  };
}

export type GlobalAudioVolumeSlider = ReturnType<typeof useGlobalAudioVolumeSlider>;
